import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from '@shoflo/polymer/components/Button/Button';
import { Input } from '@shoflo/polymer/components/Input/Input';
import { InputLabel } from '@shoflo/polymer/components/InputLabel/InputLabel';
import { UserValidator } from '@shoflo/polymer/validators/UserValidator/UserValidator';
import { PasswordTooltip } from '@shoflo/polymer/components/PasswordTooltip/PasswordTooltip';
import { shouldHaveFeature } from '@shoflo/polymer/helpers/Feature/Feature';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { Form, FormGroup } from 'reactstrap';
import PropTypes from 'prop-types';
import { signUpNew } from 'actions/user';
import styled from 'styled-components';
import moment from 'moment-timezone';

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const FullNameContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 5px;
`;

const WatchedPasswordTooltip = ({ control, errors }) => {
  const password = useWatch({
    control,
    name: 'password', // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both
    defaultValue: '' // default value before the render
  });

  if (!errors.password) return null;

  return (
    <PasswordTooltip
      targetElement="password"
      password={password}
      placement="top-end"
    />
  );
};

WatchedPasswordTooltip.propTypes = {
  control: PropTypes.object,
  errors: PropTypes.object
};

function isValidName(val) {
  return UserValidator.name(val) || 'Uh oh! A name is required';
}

function isRequiredNameLength(val) {
  return (
    UserValidator.nameLength(val) || 'Uh oh! Name requires 2 to 50 characters'
  );
}

function noForbiddenCharacters(val) {
  return (
    UserValidator.forbiddenXSSCharacters(val) ||
    'Uh oh! Illegal characters detected'
  );
}
function isNotUrl(val) {
  return !UserValidator.isUrl(val) || 'Uh oh! Cannot be a link';
}

export const UnconnectedSignUpForm = ({ signUpNew, newTeam, signUpError }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { control, register, handleSubmit } = useForm({
    mode: 'all'
  });
  const submitText = !isSubmitting
    ? 'Complete sign up'
    : 'Good things are happening...';
  const { errors, isValid } = useFormState({ control });
  /** @todo: remove flag once new UX is default */
  const isSignupNameSplit = shouldHaveFeature('Shoflo.SignupName');
  const userTimezone = moment.tz.guess(true);

  useEffect(() => {
    if (newTeam || !signUpError) {
      setIsSubmitting(false);
    }
  }, [newTeam, signUpError]);

  const submitDisabled = !isValid;

  return (
    <Form
      onSubmit={handleSubmit(async formValues => {
        formValues = {
          industry: 'Corporate',
          timezone: userTimezone,
          ...formValues
        };

        setIsSubmitting(true);
        signUpNew(formValues);
      })}
    >
      {isSignupNameSplit ? (
        <FullNameContainer>
          <FormGroup>
            <InputLabel for="firstName">First name</InputLabel>
            <Input
              id="firstName"
              data-testid="firstName-input"
              placeholder="e.g. John"
              error={errors.firstName}
              {...register('firstName', {
                validate: {
                  isValidName,
                  isRequiredNameLength,
                  noForbiddenCharacters,
                  isNotUrl
                }
              })}
            />
          </FormGroup>
          <FormGroup>
            <InputLabel for="lastName">Last name</InputLabel>
            <Input
              id="lastName"
              data-testid="lastName-input"
              placeholder="e.g. Doe"
              error={errors.lastName}
              {...register('lastName', {
                validate: {
                  isValidName,
                  isRequiredNameLength,
                  noForbiddenCharacters,
                  isNotUrl
                }
              })}
            />
          </FormGroup>
        </FullNameContainer>
      ) : (
        <FormGroup>
          <InputLabel for="fullName">Full name</InputLabel>
          <Input
            id="fullName"
            data-testid="fullName-input"
            placeholder="e.g. Tim Bergling"
            error={errors.fullName}
            {...register('fullName', {
              required: true,
              validate: {
                isValid: val =>
                  UserValidator.fullName(val) ||
                  'Uh oh! A first and last name is required',
                isNotUrl,
                noForbiddenCharacters
              }
            })}
          />
        </FormGroup>
      )}
      <FormGroup>
        <InputLabel for="email">Email</InputLabel>
        <Input
          id="email"
          name="email"
          data-testid="email-input"
          placeholder="e.g. tim.berg@gmail.com"
          error={errors.email}
          {...register('email', {
            validate: {
              invalid: val =>
                UserValidator.email(val) || 'Uh oh! Email is invalid'
            }
          })}
          autoComplete="email"
        />
      </FormGroup>
      <FormGroup>
        <InputLabel for="password">Password</InputLabel>
        <Input
          id="password"
          name="password"
          data-testid="password-input"
          type="password"
          placeholder="Enter a password"
          error={errors.password}
          {...register('password', {
            required: true,
            validate: val =>
              UserValidator.password(val) || 'Uh oh! Password is invalid'
          })}
          autoComplete="new-password"
        />
        <WatchedPasswordTooltip control={control} errors={errors} />
      </FormGroup>
      <FormGroup>
        <InputLabel for="teamName">Team or Company Name</InputLabel>
        <Input
          id="teamName"
          data-testid="teamName-input"
          placeholder="e.g. xyz.Productions"
          error={errors.teamName}
          {...register('teamName', {
            required: true,
            validate: {
              isValidLength: val =>
                UserValidator.teamName(val) ||
                'Uh oh! 2 to 32 characters are required',
              isNotUrl,
              noForbiddenCharacters
            }
          })}
        />
      </FormGroup>
      <FormGroup>
        <InputLabel for="referral">How did you hear about Shoflo</InputLabel>
        <Input
          id="referral"
          data-testid="referral-input"
          placeholder="used it on an event, search engine, friend, social media, etc."
          error={errors.referral}
          {...register('referral', {
            required: true,
            validate: val =>
              UserValidator.heardAbout(val) ||
              'Uh oh! 2 to 300 characters are required'
          })}
        />
      </FormGroup>

      <StyledButtonContainer>
        <Button
          data-testid="complete-signup-button"
          type="submit"
          disabled={isSubmitting || submitDisabled}
        >
          {submitText}
        </Button>
      </StyledButtonContainer>
    </Form>
  );
};

UnconnectedSignUpForm.propTypes = {
  signUpNew: PropTypes.func,
  newTeam: PropTypes.object,
  signUpError: PropTypes.bool
};

export default connect(
  ({ newTeam, signUpError }) => ({ newTeam, signUpError }),
  dispatch => {
    return {
      signUpNew: (...args) => dispatch(signUpNew(...args))
    };
  }
)(UnconnectedSignUpForm);
